import React, { useRef, useEffect, useState } from 'react';
import { useEditMode } from '../contexts/EditModeContext';

const PRESET_COLORS = [
  { name: 'Default', value: '' },
  { name: 'Charcoal', value: '#222222' },
  { name: 'Sleek Gray', value: '#666666' },
  { name: 'Elegant Gold', value: '#d4af37' },
  { name: 'Rose Gold', value: '#c88e8d' },
  { name: 'Sage Green', value: '#8a9a86' },
  { name: 'Navy Blue', value: '#1a2e40' },
  { name: 'Terracotta', value: '#c06c5c' },
  { name: 'Soft Cream', value: '#fdfbf7' },
  { name: 'Pure White', value: '#ffffff' }
];

const THEME_COLORS = [
  { name: 'Primary', value: 'var(--color-primary)', hex: '#6b8e6f' },
  { name: 'Accent', value: 'var(--color-accent)', hex: '#d4a574' },
  { name: 'Text', value: 'var(--color-text)', hex: '#3d3d3d' },
  { name: 'Text Light', value: 'var(--color-text-light)', hex: '#8a8a8a' }
];

const FONT_OPTIONS = ['Gotham', 'Halimun', 'RegularText', 'Cursive', 'Cursive2', 'HeroNames', 'Date', 'serif', 'sans-serif', 'monospace'];

const mapSizeToOption = (sizeVal) => {
  if (!sizeVal) return '';
  const sizeStr = String(sizeVal).toLowerCase();
  
  if (['1', '2', '3', '4', '5', '6', '7'].includes(sizeStr)) {
    return sizeStr;
  }
  
  if (sizeStr.includes('px')) {
    const px = parseInt(sizeStr, 10);
    if (px <= 11) return '1';
    if (px <= 14) return '2';
    if (px <= 17) return '3';
    if (px <= 20) return '4';
    if (px <= 28) return '5';
    if (px <= 38) return '6';
    return '7';
  }
  
  if (sizeStr.includes('x-small')) return '1';
  if (sizeStr.includes('small')) return '2';
  if (sizeStr.includes('medium')) return '3';
  if (sizeStr.includes('x-large')) return '5';
  if (sizeStr.includes('xx-large')) return '6';
  if (sizeStr.includes('large')) return '4';
  
  return '';
};

function EditableText({ value, onChange, className = '', tag = 'div', style = {}, html = false, multiline = false, rows = 10 }) {
  const { isEditMode } = useEditMode();
  const ref = useRef(null);
  const wrapperRef = useRef(null);
  const savedRangeRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [activeColorPicker, setActiveColorPicker] = useState(false);
  const [currentFont, setCurrentFont] = useState('');
  const [currentSize, setCurrentSize] = useState('');
  const [currentTransform, setCurrentTransform] = useState('none');

  useEffect(() => {
    if (ref.current) {
      const currentValue = html ? ref.current.innerHTML : ref.current.textContent;
      if (currentValue !== value) {
        if (html) {
          ref.current.innerHTML = value || '';
        } else {
          ref.current.textContent = value || '';
        }
      }
    }
  }, [value, html]);

  const handleBlur = () => {
    if (ref.current) {
      if (html) {
        const newValue = ref.current.innerHTML;
        if (newValue !== value) {
          onChange(newValue);
        }
      } else {
        // Create temporary div to decode HTML entities
        const temp = document.createElement('div');
        temp.innerHTML = ref.current.innerHTML
          .replace(/<br\s*\/?>/gi, '\n')
          .replace(/<div>/gi, '\n')
          .replace(/<\/div>/gi, '');
        const text = temp.textContent || temp.innerText || '';
        if (text !== value) {
          onChange(text);
        }
      }
    }
  };

  const handleBlurWrapper = (e) => {
    const nextActive = e.relatedTarget || document.activeElement;
    
    // Check if focus is moving to something inside our wrapper
    if (wrapperRef.current && wrapperRef.current.contains(nextActive)) {
      return;
    }
    
    // Otherwise, focus is moving outside the component entirely. Blur immediately!
    setIsFocused(false);
    setActiveColorPicker(false);
    handleBlur();
  };

  const updateActiveStyles = () => {
    try {
      const sel = window.getSelection();
      let font = document.queryCommandValue('fontName') || '';
      font = font.replace(/['"]/g, '').trim().toLowerCase();
      const matchedFont = FONT_OPTIONS.find(f => font.includes(f.toLowerCase())) || '';
      setCurrentFont(matchedFont);
      
      let size = document.queryCommandValue('fontSize') || '';
      setCurrentSize(mapSizeToOption(size));

      let transform = 'none';
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        let node = range.commonAncestorContainer;
        if (node.nodeType === 3) {
          node = node.parentNode;
        }
        transform = window.getComputedStyle(node).textTransform || 'none';
      } else if (ref.current) {
        transform = window.getComputedStyle(ref.current).textTransform || 'none';
      }
      if (!['none', 'uppercase', 'capitalize', 'lowercase'].includes(transform)) {
        transform = 'none';
      }
      setCurrentTransform(transform);
    } catch (e) {
      // ignore errors
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    setTimeout(updateActiveStyles, 50);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      document.execCommand('insertLineBreak');
    }
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      if (ref.current && ref.current.contains(range.commonAncestorContainer)) {
        savedRangeRef.current = range;
        updateActiveStyles();
      }
    }
  };

  const restoreSelection = () => {
    if (savedRangeRef.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedRangeRef.current);
    }
  };

  const applyCommand = (command, value = null) => {
    restoreSelection();
    // Ensure styling outputs standard CSS styling inside spans
    document.execCommand('styleWithCSS', false, true);

    const sel = window.getSelection();
    if (sel) {
      const selectedText = sel.toString().trim();
      if (selectedText.length > 0) {
        // Apply styling directly to selection
        document.execCommand(command, false, value);
      } else {
        // Fallback: Select all text contents, format them, then collapse cursor to the end
        const range = document.createRange();
        range.selectNodeContents(ref.current);
        sel.removeAllRanges();
        sel.addRange(range);
        
        document.execCommand(command, false, value);
        
        sel.collapseToEnd();
      }
      
      // Update saved selection range with current cursor position
      if (sel.rangeCount > 0) {
        savedRangeRef.current = sel.getRangeAt(0).cloneRange();
      }

      // Instantly trigger change capture
      if (ref.current) {
        onChange(ref.current.innerHTML);
      }
      updateActiveStyles();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const clipboardData = e.clipboardData || (e.originalEvent && e.originalEvent.clipboardData);
    if (!clipboardData) return;
    
    const text = clipboardData.getData('text/plain');
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    
    const range = sel.getRangeAt(0);
    range.deleteContents();
    
    const textNode = document.createTextNode(text);
    range.insertNode(textNode);
    
    // Move cursor to the end of the pasted text
    range.setStartAfter(textNode);
    range.setEndAfter(textNode);
    sel.removeAllRanges();
    sel.addRange(range);
    
    // Trigger state change
    if (ref.current) {
      onChange(ref.current.innerHTML);
    }
  };

  const applyTextTransform = (transformType) => {
    restoreSelection();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    
    const range = sel.getRangeAt(0);
    const selectedText = sel.toString();
    
    if (selectedText.length > 0) {
      const span = document.createElement('span');
      span.style.textTransform = transformType;
      span.textContent = selectedText;
      
      range.deleteContents();
      range.insertNode(span);
      
      const newRange = document.createRange();
      newRange.selectNode(span);
      sel.removeAllRanges();
      sel.addRange(newRange);
    } else {
      if (ref.current) {
        ref.current.style.textTransform = transformType;
      }
    }
    
    if (ref.current) {
      onChange(ref.current.innerHTML);
    }
    updateActiveStyles();
  };

  const cycleCasing = () => {
    const transforms = ['none', 'uppercase', 'capitalize', 'lowercase'];
    const currentIndex = transforms.indexOf(currentTransform);
    const nextIndex = (currentIndex + 1) % transforms.length;
    const nextTransform = transforms[nextIndex];
    
    applyTextTransform(nextTransform);
  };

  const cycleAlignment = () => {
    const isCenter = document.queryCommandState('justifyCenter');
    const isRight = document.queryCommandState('justifyRight');

    if (isCenter) {
      applyCommand('justifyRight');
    } else if (isRight) {
      applyCommand('justifyLeft');
    } else {
      applyCommand('justifyCenter');
    }
  };

  const Tag = tag;

  if (!isEditMode) {
    if (html) {
      return <Tag className={className} style={{ ...style, whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: value || '' }} />;
    }
    return <Tag className={className} style={{ ...style, whiteSpace: 'pre-wrap' }}>{value}</Tag>;
  }

  return (
    <div ref={wrapperRef} className="editable-text-wrapper" style={{ position: 'relative', width: '100%' }}>
      {html && isFocused && (
        <div className="text-actions-menu-floating">
          {/* Font Family Selector Dropdown */}
          <select
            value={currentFont}
            onChange={(e) => {
              e.preventDefault();
              applyCommand('fontName', e.target.value);
            }}
            className="cell-action-menu-select font-family-select"
            title="Set Font Family"
          >
            <option value="">Font: Default</option>
            <option value="Gotham">Gotham</option>
            <option value="Halimun">Halimun</option>
            <option value="RegularText">Regular Text</option>
            <option value="Cursive">Cursive</option>
            <option value="Cursive2">Cursive Elegant</option>
            <option value="HeroNames">Hero Names</option>
            <option value="Date">Date/Display</option>
            <option value="serif">Serif</option>
            <option value="sans-serif">Sans-Serif</option>
            <option value="monospace">Monospace</option>
          </select>

          {/* Alignment Selector */}
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              cycleAlignment();
            }}
            className="cell-action-menu-btn"
            title="Align Text (Left/Center/Right)"
            type="button"
          >
            ⇹ Align
          </button>

          {/* Font Size Selector Dropdown (using standard size index mapping) */}
          <select
            value={currentSize}
            onChange={(e) => {
              e.preventDefault();
              // Maps standard sizes to CSS size outputs
              applyCommand('fontSize', e.target.value);
            }}
            className="cell-action-menu-select font-size-select"
            title="Set Font Size"
          >
            <option value="">Size: Default</option>
            <option value="1">8 pt</option>
            <option value="2">10 pt</option>
            <option value="3">12 pt</option>
            <option value="4">14 pt</option>
            <option value="5">18 pt</option>
            <option value="6">24 pt</option>
            <option value="7">36 pt</option>
          </select>

          {/* Bold */}
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              applyCommand('bold');
            }}
            className="cell-action-menu-btn bold-btn"
            title="Bold"
            type="button"
            style={{ fontWeight: 'bold' }}
          >
            B
          </button>

          {/* Italic */}
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              applyCommand('italic');
            }}
            className="cell-action-menu-btn italic-btn"
            title="Italic"
            type="button"
            style={{ fontStyle: 'italic' }}
          >
            I
          </button>

          {/* Underline */}
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              applyCommand('underline');
            }}
            className="cell-action-menu-btn underline-btn"
            title="Underline"
            type="button"
            style={{ textDecoration: 'underline' }}
          >
            U
          </button>

          {/* Capitalisation Case Selector Button */}
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              cycleCasing();
            }}
            className={`cell-action-menu-btn case-btn ${currentTransform && currentTransform !== 'none' ? 'active' : ''}`}
            title={`Text Case: ${
              currentTransform === 'uppercase' ? 'UPPERCASE' :
              currentTransform === 'capitalize' ? 'Capitalize' :
              currentTransform === 'lowercase' ? 'lowercase' :
              'Normal'
            }`}
            type="button"
          >
            🔠 {
              currentTransform === 'uppercase' ? 'AA' :
              currentTransform === 'capitalize' ? 'Aa' :
              currentTransform === 'lowercase' ? 'aa' :
              'Case'
            }
          </button>

          {/* Color Picker */}
          <div className="cell-action-menu-btn color-picker-btn" title="Set Font Color">
            <button
              type="button"
              className="color-picker-toggle-btn"
              onMouseDown={(e) => {
                e.preventDefault();
                setActiveColorPicker(!activeColorPicker);
              }}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '0.7rem',
                color: 'inherit'
              }}
            >
              🎨 Color
            </button>

            {activeColorPicker && (
              <div className="cell-color-popover" onMouseDown={(e) => e.preventDefault()} onClick={(e) => e.stopPropagation()}>
                <div className="color-popover-header">
                  <span className="color-popover-title">Text Color</span>
                  <button
                    type="button"
                    className="color-popover-close-btn"
                    onClick={() => setActiveColorPicker(false)}
                  >
                    ×
                  </button>
                </div>
                
                <div className="color-section-label">Theme Colors</div>
                <div className="color-presets-grid theme-colors-grid">
                  {THEME_COLORS.map((col) => (
                    <button
                      key={col.name}
                      type="button"
                      className="color-preset-dot"
                      style={{ background: col.hex }}
                      onClick={() => {
                        applyCommand('foreColor', col.hex);
                        setActiveColorPicker(false);
                      }}
                      title={col.name}
                    />
                  ))}
                </div>
                
                <div className="color-section-label">Presets</div>
                <div className="color-presets-grid">
                  {PRESET_COLORS.map((col) => (
                    <button
                      key={col.name}
                      type="button"
                      className="color-preset-dot"
                      style={{ background: col.value || '#eeeeee' }}
                      onClick={() => {
                        applyCommand('foreColor', col.value || '#333333');
                        setActiveColorPicker(false);
                      }}
                      title={col.name}
                    >
                      {col.value === '' && <span className="default-slash">/</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <Tag
        ref={ref}
        className={`${className} editable`}
        style={{ ...style, whiteSpace: 'pre-wrap' }}
        contentEditable
        suppressContentEditableWarning
        onBlur={handleBlurWrapper}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onMouseUp={saveSelection}
        onKeyUp={saveSelection}
        dangerouslySetInnerHTML={html ? { __html: value || '' } : undefined}
      >
        {!html ? value : undefined}
      </Tag>
    </div>
  );
}

export default EditableText;
